import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';

import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import Sparkline from '../gui/sparkline';

/**
 * Race class statistics overview, opened from the race class detail page
 * (admin menu entry or the stats bar). Loads GET /api/raceclasses/:id/stats.
 *
 * Only usage log events and signups are available per race class; page
 * sessions (devices, session duration) are logged per race only and are
 * therefore not shown here. Unique visitors and the geo sections (map,
 * country, city) come from the ip stamped on each usage log event, the same
 * breakdown the race stats drawer shows.
 */

function formatNumber(num) {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('nb-NO');
}

function getCountryFlag(countryCode) {
    if (!countryCode || countryCode.length !== 2) return '';
    // Convert country code to flag emoji (regional indicator symbols)
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
}

const PERIODS = [
    { key: 'today', label: 'Today' },
    { key: 'all', label: 'All time' },
    { key: 'duringRace', label: 'During race', needsDates: true },
    { key: 'custom', label: 'Custom' },
];

@observer
class RaceClassStatsDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            stats: null,
            isLoading: true,
            error: null,
            period: 'today',
            customStartDate: '',
            customEndDate: '',
        };
    }

    componentDidMount() {
        this.loadStats();
    }

    loadStats = async (periodOverride) => {
        const { raceClassId } = this.props;
        const { period, customStartDate, customEndDate } = this.state;
        const activePeriod = periodOverride || period;

        if (!raceClassId) {
            this.setState({ error: 'No race class ID provided', isLoading: false });
            return;
        }

        this.setState({ isLoading: true });
        try {
            let url = `/api/raceclasses/${raceClassId}/stats?period=${activePeriod}`;
            if (activePeriod === 'custom' && customStartDate && customEndDate) {
                url += `&startDate=${customStartDate}&endDate=${customEndDate}`;
            }
            const response = await util.fetchApi(url, { method: 'GET' });
            if (response.status === 200) {
                this.setState({ stats: response.data, isLoading: false, error: null });
            } else {
                this.setState({ error: response.message || 'Failed to load stats', isLoading: false });
            }
        } catch (err) {
            this.setState({ error: err.message, isLoading: false });
        }
    }

    handlePeriodClick = (e) => {
        const { period } = e.currentTarget.dataset;
        this.setState({ period }, () => {
            if (period !== 'custom') {
                this.loadStats(period);
            }
        });
    }

    handleDateChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
    }

    handleCustomDateSubmit = () => {
        const { customStartDate, customEndDate } = this.state;
        if (customStartDate && customEndDate) {
            this.loadStats('custom');
        }
    }

    renderTrend(trend) {
        if (!trend || trend === 0) return null;
        const isPositive = trend > 0;
        return (
            <span class={`ms-1 ${isPositive ? 'text-success' : 'text-danger'}`}>
                {isPositive ? '↑' : '↓'}{Math.abs(trend)}%
            </span>
        );
    }

    renderSummaryCard(title, value, icon, trend, darkmode) {
        return (
            <div class='col-6 col-md-3 mb-3'>
                <div class={`rounded-lg p-3 h-100 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                    <div class='text-muted small mb-1'>
                        <i class={`fa-solid ${icon} me-1`} />{title}
                    </div>
                    <div class='d-flex align-items-baseline'>
                        <span class='h4 mb-0 font-weight-bold'>{typeof value === 'number' ? formatNumber(value) : value}</span>
                        {this.renderTrend(trend)}
                    </div>
                </div>
            </div>
        );
    }

    renderPeriodSelector(darkmode, raceClass) {
        const { period, customStartDate, customEndDate, isLoading } = this.state;
        const hasDates = !!(raceClass?.startDate && raceClass?.endDate);

        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <div class='d-flex flex-wrap align-items-center gap-2'>
                    <span class='text-muted small me-2'>
                        <i class='fa-solid fa-filter me-1' />
                        Period:
                    </span>
                    <div class='btn-group btn-group-sm'>
                        {PERIODS.filter(p => !p.needsDates || hasDates).map(p => (
                            <button
                                key={p.key}
                                type='button'
                                data-period={p.key}
                                class={`btn ${period === p.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={this.handlePeriodClick}
                                disabled={isLoading}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>

                    {period === 'custom' && (
                        <div class='d-flex align-items-center gap-2 ms-2'>
                            <input
                                type='date'
                                name='customStartDate'
                                class='form-control form-control-sm'
                                style='width: 140px;'
                                value={customStartDate}
                                onChange={this.handleDateChange}
                            />
                            <span class='text-muted'>to</span>
                            <input
                                type='date'
                                name='customEndDate'
                                class='form-control form-control-sm'
                                style='width: 140px;'
                                value={customEndDate}
                                onChange={this.handleDateChange}
                            />
                            <button
                                type='button'
                                class='btn btn-sm btn-primary'
                                onClick={this.handleCustomDateSubmit}
                                disabled={!customStartDate || !customEndDate || isLoading}
                            >
                                <i class='fa-solid fa-search' />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    renderDailyChart(dailyStats) {
        if (!dailyStats || dailyStats.length === 0) {
            return (
                <div class='text-muted text-center py-4'>
                    No daily data available yet
                </div>
            );
        }

        const dayTotal = d => (d.views || 0) + (d.resultViews || 0) + (d.liveTrackingViews || 0);
        const maxViews = Math.max(...dailyStats.map(dayTotal));
        const displayData = dailyStats.slice(-30);
        const barWidth = Math.floor(100 / Math.min(displayData.length, 30));

        return (
            <div class='d-flex align-items-end justify-content-between' style='height: 120px; overflow-x: auto;'>
                {displayData.map((day, idx) => {
                    const total = dayTotal(day);
                    const pct = value => (maxViews > 0 ? ((value || 0) / maxViews) * 100 : 0);
                    const dateLabel = day.date ? day.date.split('-')[2] : idx + 1;
                    return (
                        <div
                            key={day.date || idx}
                            class='d-flex flex-column align-items-center flex-shrink-0'
                            style={`width: ${barWidth}%; min-width: 12px; max-width: 24px;`}
                            title={`${day.date}: ${formatNumber(total)} total`}
                        >
                            <div class='d-flex flex-column-reverse' style='height: 100px; width: 100%;'>
                                <div class='bg-primary' style={`height: ${pct(day.views)}%; min-height: ${day.views > 0 ? '2px' : '0'};`} />
                                <div class='bg-success' style={`height: ${pct(day.resultViews)}%; min-height: ${day.resultViews > 0 ? '2px' : '0'};`} />
                                <div class='bg-info' style={`height: ${pct(day.liveTrackingViews)}%; min-height: ${day.liveTrackingViews > 0 ? '2px' : '0'};`} />
                            </div>
                            <div class='text-muted small mt-1' style='font-size: 0.55rem;'>
                                {dateLabel}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderPeakHours(peakHours) {
        if (!peakHours || peakHours.length === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No peak hours data available
                </div>
            );
        }

        const maxViews = Math.max(...peakHours.map(p => p.views || 0));
        const chartHeight = 80;

        return (
            <div>
                <div class='d-flex align-items-end justify-content-between' style={`height: ${chartHeight}px;`}>
                    {peakHours.map((hour) => {
                        const barHeight = maxViews > 0
                            ? Math.max(((hour.views || 0) / maxViews) * chartHeight, 2)
                            : 2;
                        const isPeak = maxViews > 0 && hour.views === maxViews;
                        return (
                            <div
                                key={hour.hour}
                                class='d-flex flex-column justify-content-end'
                                style={`width: 4%; min-width: 8px; height: ${chartHeight}px;`}
                                title={`${hour.hour}:00 - ${formatNumber(hour.views)} views`}
                            >
                                <div
                                    class={isPeak ? 'bg-warning' : 'bg-info'}
                                    style={`height: ${barHeight}px; width: 100%; border-radius: 2px 2px 0 0;`}
                                />
                            </div>
                        );
                    })}
                </div>
                <div class='d-flex justify-content-between text-muted small mt-1'>
                    <span>00:00</span>
                    <span>06:00</span>
                    <span>12:00</span>
                    <span>18:00</span>
                    <span>23:00</span>
                </div>
            </div>
        );
    }

    renderProgressRows(items) {
        return (
            <div>
                {items.map(item => (
                    <div class='mb-2' key={item.label}>
                        <div class='d-flex justify-content-between small mb-1'>
                            <span>{item.label}</span>
                            <span class='text-muted'>{item.text}</span>
                        </div>
                        <div class='progress' style='height: 8px;'>
                            <div class={`progress-bar ${item.color}`} style={`width: ${item.percent}%;`} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    renderTrafficBreakdown(breakdown) {
        return this.renderProgressRows([
            {
                label: 'Class info', percent: breakdown?.info || 0, text: `${breakdown?.info || 0}%`, color: 'bg-primary',
            },
            {
                label: 'Results', percent: breakdown?.results || 0, text: `${breakdown?.results || 0}%`, color: 'bg-success',
            },
            {
                label: 'Live tracking', percent: breakdown?.tracking || 0, text: `${breakdown?.tracking || 0}%`, color: 'bg-info',
            },
        ]);
    }

    renderGeoRows(rows, labelFn, color, emptyText) {
        if (!rows || rows.length === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    {emptyText}
                </div>
            );
        }
        const maxVisitors = Math.max(...rows.map(r => r.visitors || 0));
        return (
            <div>
                {rows.map((row, idx) => {
                    const barWidth = maxVisitors > 0 ? ((row.visitors || 0) / maxVisitors) * 100 : 0;
                    return (
                        <div class='mb-2' key={`${labelFn(row)}-${idx}`}>
                            <div class='d-flex justify-content-between small mb-1'>
                                <span>
                                    {row.countryCode && <span class='me-2'>{getCountryFlag(row.countryCode)}</span>}
                                    {labelFn(row)}
                                </span>
                                <span class='text-muted'>{formatNumber(row.visitors)} ({row.percentage || 0}%)</span>
                            </div>
                            <div class='progress' style='height: 8px;'>
                                <div class={`progress-bar ${color}`} style={`width: ${barWidth}%;`} />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderCountryBreakdown(countryBreakdown) {
        return this.renderGeoRows(countryBreakdown, c => c.country, 'bg-primary', 'No country data available');
    }

    renderCityBreakdown(cityBreakdown) {
        return this.renderGeoRows(
            cityBreakdown,
            c => `${c.city}${c.country ? ` (${c.country})` : ''}`,
            'bg-info',
            'No city data available',
        );
    }

    renderVisitorMap(cityBreakdown) {
        const cities = (cityBreakdown || []).filter(c => Number.isFinite(c.lat) && Number.isFinite(c.lon));
        if (cities.length === 0) {
            return null;
        }
        const maxVisitors = Math.max(...cities.map(c => c.visitors));
        const bounds = cities.map(c => [c.lat, c.lon]);

        return (
            <div class='rounded-lg overflow-hidden mb-3' style='height: 300px;'>
                <MapContainer
                    bounds={bounds}
                    boundsOptions={{ padding: [40, 40], maxZoom: 10 }}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                    />
                    {cities.map(c => (
                        <CircleMarker
                            key={`${c.city}-${c.countryCode}`}
                            center={[c.lat, c.lon]}
                            radius={6 + Math.round(14 * Math.sqrt(c.visitors / maxVisitors))}
                            pathOptions={{ color: '#0d6efd', fillColor: '#0d6efd', fillOpacity: 0.4 }}
                        >
                            <Popup>
                                <strong>{c.city}</strong>{c.country ? `, ${c.country}` : ''}<br />
                                {formatNumber(c.visitors)} {c.visitors === 1 ? 'visitor' : 'visitors'}
                            </Popup>
                        </CircleMarker>
                    ))}
                </MapContainer>
            </div>
        );
    }

    renderSignupBreakdown(signups) {
        const total = signups?.total || 0;
        if (total === 0) {
            return (
                <div class='text-muted text-center py-3'>
                    No signups yet
                </div>
            );
        }
        const row = (label, value, color) => ({
            label,
            percent: Math.round(((value || 0) / total) * 100),
            text: `${formatNumber(value || 0)} (${Math.round(((value || 0) / total) * 100)}%)`,
            color,
        });
        return this.renderProgressRows([
            row('Approved', signups.approved, 'bg-success'),
            row('Pending approval', signups.pending, 'bg-warning'),
            row('Paid signup fee', signups.paidSignupfee, 'bg-primary'),
            row('Paid signup', signups.paidSignup, 'bg-primary'),
            row('Paid rest', signups.paidRest, 'bg-primary'),
            row('Finished', signups.finished, 'bg-info'),
            row('Scratched', signups.scratched, 'bg-danger'),
            row('DNS', signups.dns, 'bg-secondary'),
        ]);
    }

    render() {
        const { stats, isLoading, error, period } = this.state;
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (isLoading && !stats) {
            return (
                <div class='w-100 d-flex justify-content-center align-items-center' style='min-height: 200px;'>
                    <div class='spinner-border text-primary' role='status'>
                        <span class='visually-hidden'>Loading...</span>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div class='w-100 p-4'>
                    <div class='alert alert-danger'>{error}</div>
                </div>
            );
        }

        if (!stats) {
            return (
                <div class='w-100 p-4'>
                    <div class='alert alert-warning'>No statistics available</div>
                </div>
            );
        }

        const {
            raceClass, race, summary, weeklyStats, dailyStats, peakHours, trafficBreakdown, signups,
            countryBreakdown, cityBreakdown,
        } = stats;
        const visitorMap = this.renderVisitorMap(cityBreakdown);

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding: 20px; padding-bottom: 100px;'>

                    <div class='mb-4'>
                        <h5 class='mb-0'>
                            <i class='fa-solid fa-chart-line me-2' />
                            Statistics: {raceClass?.title}
                            {isLoading && <span class='spinner-border spinner-border-sm ms-2' />}
                        </h5>
                        {race?.title && (
                            <div class='text-muted small'>{race.title}{race.year ? ` ${race.year}` : ''}</div>
                        )}
                    </div>

                    {this.renderPeriodSelector(darkmode, raceClass)}

                    <div class='row'>
                        {this.renderSummaryCard('Total Views', summary?.totalViews, 'fa-eye', period === 'all' ? summary?.viewsTrend : null, darkmode)}
                        {this.renderSummaryCard('Results Views', summary?.totalResultViews, 'fa-trophy', null, darkmode)}
                        {this.renderSummaryCard('Tracking Views', summary?.totalLiveTrackingViews, 'fa-map-location-dot', null, darkmode)}
                        {this.renderSummaryCard('Signups', summary?.totalSignups, 'fa-user-plus', null, darkmode)}
                        {this.renderSummaryCard('Unique Visitors', summary?.uniqueVisitors, 'fa-users', null, darkmode)}
                        {this.renderSummaryCard('Logged In', summary?.loggedInUsers, 'fa-user-check', null, darkmode)}
                        {this.renderSummaryCard('Peak Hour', summary?.peakHour === null || summary?.peakHour === undefined ? '-' : `${summary.peakHour}:00`, 'fa-chart-simple', null, darkmode)}
                    </div>

                    {weeklyStats?.length > 1 && (
                        <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <h6 class='mb-3'>
                                <i class='fa-solid fa-chart-simple me-2' />
                                Weekly Trend
                            </h6>
                            <Sparkline
                                data={weeklyStats}
                                valueField='views'
                                height={40}
                                barWidth={10}
                                barGap={3}
                                maxBars={26}
                            />
                        </div>
                    )}

                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <div class='d-flex justify-content-between align-items-center mb-3'>
                            <h6 class='mb-0'>
                                <i class='fa-solid fa-chart-bar me-2' />
                                Daily Traffic (Last 30 Days)
                            </h6>
                            <div class='d-flex gap-3 small'>
                                <span><span class='badge bg-primary'>&nbsp;</span> Info</span>
                                <span><span class='badge bg-success'>&nbsp;</span> Results</span>
                                <span><span class='badge bg-info'>&nbsp;</span> Tracking</span>
                            </div>
                        </div>
                        {this.renderDailyChart(dailyStats)}
                    </div>

                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-clock me-2' />
                            Peak Hours (Views by Hour)
                        </h6>
                        {this.renderPeakHours(peakHours)}
                    </div>

                    <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-chart-pie me-2' />
                            Traffic Breakdown
                        </h6>
                        {this.renderTrafficBreakdown(trafficBreakdown)}
                    </div>

                    {visitorMap && (
                        <div class={`raceclass-stats-map rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <h6 class='mb-3'>
                                <i class='fa-solid fa-map-location-dot me-2' />
                                Visitor Map
                            </h6>
                            {visitorMap}
                        </div>
                    )}

                    <div class='row'>
                        <div class='col-12 col-md-6'>
                            <div class={`raceclass-stats-countries rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                <h6 class='mb-3'>
                                    <i class='fa-solid fa-globe me-2' />
                                    Visitors by Country
                                </h6>
                                {this.renderCountryBreakdown(countryBreakdown)}
                            </div>
                        </div>
                        <div class='col-12 col-md-6'>
                            <div class={`raceclass-stats-cities rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                <h6 class='mb-3'>
                                    <i class='fa-solid fa-city me-2' />
                                    Visitors by City
                                </h6>
                                {this.renderCityBreakdown(cityBreakdown)}
                            </div>
                        </div>
                    </div>

                    <div class={`raceclass-stats-signups rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-user-plus me-2' />
                            Signups
                        </h6>
                        {this.renderSignupBreakdown(signups)}
                    </div>

                </div>
            </div>
        );
    }
}

export default RaceClassStatsDrawer;
